/**
 * ReconcileCells — cell components for the order-confirmation / reconciliation
 * flow (REQ-order-confirmation-reconciliation).
 *
 *  - OrderModalityCell: Orders-page modality cell. Shows the resolved modality
 *    pill; when po_modality is empty (no items_master match — the ingest-time
 *    keyword/section guessers are suppressed) it shows "N/A" plus an inline
 *    "Check" button that re-resolves the modality on demand after an admin adds
 *    the missing item code (POST /recheck-order-modality).
 *
 *  - HisStatusReconcileCell: PACS/Worklist HIS-Status cell. Every status renders
 *    as a designed status pill (colour-coded, dot/check-marked); RECONCILED gets
 *    a distinct success treatment. A stored multi-field MISMATCH renders an
 *    actionable "Confirm" chip instead of a status, opening a dialog that lists
 *    the per-field scan↔order comparison and lets the user correct the accession
 *    number when that is the mismatching field (POST /confirm-order-reconciliation).
 *    It also carries the order ↔ scan adoption affordances — a blue "Confirm"
 *    (the Link Order flow) for an order-less scan, "Fix ID" when an automatic
 *    adoption could not reach the
 *    archive, plus force-reconcile / detach inside the Confirm dialog
 *    (ORDER_ADOPTION_HANDOFF.md §5).
 *
 * Both components keep their resolved result in local state (and mirror it onto
 * the row record) so the grid reflects the action immediately; the next list
 * refresh brings the canonical backend state.
 */

import React, { useState } from 'react';
import { Button, Checkbox, Input, Modal, Tooltip, message } from 'antd';
import { hasPermission, makePostCall } from '../utils/helper';
import { Permissions } from '../constants/fieldLabels';
import LinkOrderDialog from './LinkOrderDialog';

/**
 * Minutes a study may sit with its expected modalities incomplete before the
 * Confirm chip appears. Read straight off window.ENV_CONFIG rather than through
 * APP_CONSTANTS: that file is gitignored and maintained per deployment, so a
 * key added to it there would never reach other installs. env-config.js is the
 * runtime-editable file (no rebuild), and the fallback covers deployments whose
 * copy predates this setting.
 */
const RECONCILE_STALE_MINUTES: number =
  Number((typeof window !== 'undefined' && (window as any).ENV_CONFIG?.RECONCILE_STALE_MINUTES)) || 20;

/**
 * Has this study been sitting incomplete long enough to be worth a human's
 * attention? The backend deliberately never flags a modality shortfall as a
 * hard mismatch: while a study is streaming in, "received set disjoint from
 * expected" is indistinguishable from "only the first modality of a hybrid has
 * landed" (a PET-CT whose NM series ship before its PT/CT), and flagging it
 * immediately put a Confirm chip on perfectly healthy studies.
 *
 * Elapsed time is the missing signal, and it is derived here rather than
 * persisted: no backend job has to fire at the threshold, nothing can miss the
 * transition, and every grid refresh re-evaluates it for free.
 */
const isStale = (rec: any): boolean => {
  const raw = rec?.ps_scan_received_at;
  if (!raw) return false; // no arrival time -> can't judge; stay passive
  const receivedAt = Date.parse(raw);
  if (Number.isNaN(receivedAt)) return false;
  return Date.now() - receivedAt > Number(RECONCILE_STALE_MINUTES) * 60 * 1000;
};

const FIELD_LABELS: Record<string, string> = {
  modality: 'Modality',
  accession: 'Accession No',
  patient_id: 'YH No.',//Patient ID
};

/* ------------------------------------------------------------------ *
 * Scoped styles — injected once. Keeps hover/transition affordances
 * (which inline styles can't express) out of every grid cell render.
 * ------------------------------------------------------------------ */
const STYLE_ID = 'reconcile-cells-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
  .rc-pill {
    display: inline-flex; align-items: center; gap: 4px;
    height: 16px; padding: 0 7px; border-radius: 999px;
    font-size: 9px; font-weight: 600; line-height: 1;
    letter-spacing: .02em; white-space: nowrap;
    border: 1px solid transparent; user-select: none;
    transition: box-shadow .15s ease;
  }
  .rc-pill--reconciled { box-shadow: 0 1px 2px rgba(16,185,129,.15); }
  .rc-dot { width: 4px; height: 4px; border-radius: 50%; flex: none; }
  .rc-ico { flex: none; display: block; }

  .rc-confirm {
    display: inline-flex; align-items: center; gap: 4px;
    height: 16px; padding: 0 7px; border-radius: 999px;
    font-size: 9px; font-weight: 600; letter-spacing: .02em;
    cursor: pointer; color: #92400E;
    border: 1px solid #F59E0B;
    background: linear-gradient(180deg, #FEF9EC 0%, #FDECBE 100%);
    transition: box-shadow .15s ease, transform .1s ease, filter .15s ease;
  }
  .rc-confirm:hover { filter: brightness(1.02); box-shadow: 0 2px 8px rgba(245,158,11,.35); }
  .rc-confirm:active { transform: translateY(.5px); }

  /* An automatic adoption corrected the order but could not relabel the images:
     the worklist and the viewer now disagree about who the patient is. Red, not
     amber — this is the one state in the cell with a wrong-patient risk. */
  .rc-confirm--alert {
    color: #991B1B; border-color: #EF4444;
    background: linear-gradient(180deg, #FEF2F2 0%, #FECACA 100%);
  }
  .rc-confirm--alert:hover { box-shadow: 0 2px 8px rgba(239,68,68,.35); }

  /* Order-less scan: informational, not an error — the scan is fine, it just has
     no HIS order behind it yet. Blue keeps it distinct from the amber Confirm
     chip, which means something disagrees. */
  .rc-confirm--link {
    color: #1D4ED8; border-color: #93C5FD;
    background: linear-gradient(180deg, #EFF6FF 0%, #DBEAFE 100%);
  }
  .rc-confirm--link:hover { box-shadow: 0 2px 8px rgba(59,130,246,.35); }

  .rc-mod {
    display: inline-flex; align-items: center; height: 16px;
    padding: 0 6px; border-radius: 4px; font-size: 9px; font-weight: 500;
    color: #334155; background: #F1F5F9; border: 1px solid #E2E8F0;
  }
  .rc-check-btn { font-size: 9px !important; height: 14px !important; padding: 0 6px !important; border-radius: 4px !important; }

  .rc-tbl { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
  .rc-tbl thead th {
    padding: 7px 10px; text-align: left; font-weight: 600; font-size: 10.5px;
    letter-spacing: .04em; text-transform: uppercase; color: #64748B;
    background: #F8FAFC; border-bottom: 1px solid #E2E8F0;
  }
  .rc-tbl thead th:first-child { border-top-left-radius: 8px; }
  .rc-tbl thead th:last-child  { border-top-right-radius: 8px; }
  .rc-tbl tbody td { padding: 8px 10px; border-bottom: 1px solid #F1F5F9; }
  .rc-tbl tbody tr:last-child td { border-bottom: none; }
  .rc-verdict {
    display: inline-flex; align-items: center; gap: 4px;
    font-weight: 600; font-size: 11px; white-space: nowrap;
  }
  `;
  document.head.appendChild(el);
}

/* Colour-coded style per HIS status. `icon: 'check'` swaps the dot for a
 * check-mark, reserved for the RECONCILED success state. */
type PillDef = { bg: string; border: string; text: string; dot: string; icon?: 'check' };

const STATUS_STYLES: Record<string, PillDef> = {
  RECONCILED:  { bg: '#ECFDF5', border: '#A7F3D0', text: '#047857', dot: '#10B981', icon: 'check' },
  CONFIRMED:   { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', dot: '#3B82F6' },
  ACTIVE:      { bg: '#EEF2FF', border: '#C7D2FE', text: '#4338CA', dot: '#6366F1' },
  UNCONFIRMED: { bg: '#F8FAFC', border: '#E2E8F0', text: '#475569', dot: '#94A3B8' },
  CANCELLED:   { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C', dot: '#F87171' },
  NO_ITEM:     { bg: '#FFF7ED', border: '#FED7AA', text: '#C2410C', dot: '#FB923C' },
  PENDING:     { bg: '#FFFBEB', border: '#FDE68A', text: '#B45309', dot: '#F59E0B' },
};
const DEFAULT_STYLE: PillDef = STATUS_STYLES.UNCONFIRMED;

const CheckIcon = ({ color }: { color: string }) => (
  <svg className="rc-ico" width="8" height="8" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M13 4.5 6.5 11 3 7.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarnIcon = ({ color }: { color: string }) => (
  <svg className="rc-ico" width="8" height="8" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 2.2 15 14H1L8 2.2Z" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 6.4v3.1" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="8" cy="11.6" r="0.95" fill={color} />
  </svg>
);

const LinkIcon = ({ color }: { color: string }) => (
  <svg className="rc-ico" width="9" height="9" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M6.5 9.5 9.5 6.5" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    <path d="M9 4.5 10.6 2.9a2.7 2.7 0 0 1 3.8 3.8L12.8 8.3" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    <path d="M7 11.5 5.4 13.1a2.7 2.7 0 0 1-3.8-3.8L3.2 7.7" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

/**
 * "No value" for the structural order-less test. The listener writes a genuine
 * NULL for a scan-made order, but a '*' placeholder reaches these columns from
 * the migration path and from Mediff exports, and an empty string from older
 * rows — all three mean the same thing here.
 */
const isBlankish = (v: any): boolean => v == null || String(v).trim() === '' || String(v).trim() === '*';

/** A single designed status pill (dot or check + label), optionally tooltipped. */
const StatusPill = ({
  label,
  def,
  tooltip,
  status,
}: { label: string; def: PillDef; tooltip?: string | null; status?: string }) => {
  const pill = (
    <span
      className={`rc-pill${def.icon === 'check' ? ' rc-pill--reconciled' : ''}`}
      // The pill's colours are inline (they come from STATUS_STYLES), so dark mode
      // cannot simply restyle `.rc-pill` — it needs to know WHICH status this is.
      // That is what this attribute is for; styles/pacs-dark.css keys off it.
      data-rc-status={status || undefined}
      style={{ background: def.bg, borderColor: def.border, color: def.text }}
    >
      {def.icon === 'check'
        ? <CheckIcon color={def.dot} />
        : <span className="rc-dot" style={{ background: def.dot }} />}
      {label}
    </span>
  );
  return tooltip ? <Tooltip title={tooltip}>{pill}</Tooltip> : pill;
};

/**
 * Orders-page modality cell: pill, or N/A + Check when unresolved.
 *
 * `allowRecheck` gates the Check action — read-only viewers of the Orders list
 * (radiologists / HODs) see the plain N/A without the button, since resolving a
 * modality writes to the order.
 */
export const OrderModalityCell = ({ rec, allowRecheck = true }: { rec: any; allowRecheck?: boolean }) => {
  const [modality, setModality] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Prefer the row's own value (fresh list data) over the local override.
  const effective = rec?.po_modality || modality;
  if (effective) {
    return <span className="rc-mod">{effective}</span>;
  }

  if (!allowRecheck) {
    return <span style={{ color: '#94A3B8', fontSize: '9px', fontWeight: 500 }}>N/A</span>;
  }

  const handleCheck = (e: React.MouseEvent) => {
    e.stopPropagation();
    setChecking(true);
    makePostCall('/recheck-order-modality', { pacs_ord_id: rec?.pacs_ord_id })
      .then(res => {
        const data = res?.data;
        if (data?.matched && data?.modalities) {
          // Mirror onto the row record so other cells (e.g. technician
          // assignment filtering) see the resolved value without a refetch.
          if (rec) rec.po_modality = data.modalities;
          setModality(data.modalities);
          message.success(`Modality resolved: ${data.modalities}`);
        } else {
          message.info(data?.message || 'No item code is mapped for this order yet.');
        }
      })
      .catch(err => {
        console.error('recheck-order-modality failed', err);
        message.error('Failed to re-check modality');
      })
      .finally(() => setChecking(false));
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
      <span style={{ color: '#94A3B8', fontSize: '9px', fontWeight: 500 }}>N/A</span>
      <Tooltip title="No item code matched this order. After adding the item code, press Check to resolve the modality.">
        <Button className="rc-check-btn" size="small" loading={checking} onClick={handleCheck}>
          Check
        </Button>
      </Tooltip>
    </span>
  );
};

/** One row of the per-field comparison table inside the Confirm dialog. */
const CheckRow = ({ check }: { check: any }) => {
  const label = FIELD_LABELS[check?.field] || check?.field;
  let verdict: React.ReactNode;
  if (!check?.enforced) {
    verdict = <span className="rc-verdict" style={{ color: '#94A3B8' }}>not checked</span>;
  } else if (check?.matched) {
    verdict = (
      <span className="rc-verdict" style={{ color: '#047857' }}>
        <CheckIcon color="#10B981" /> match
      </span>
    );
  } else if (check?.mismatch) {
    verdict = (
      <span className="rc-verdict" style={{ color: '#B91C1C' }}>
        <WarnIcon color="#EF4444" /> mismatch
      </span>
    );
  } else {
    verdict = (
      <span className="rc-verdict" style={{ color: '#B45309' }}>
        <span className="rc-dot" style={{ background: '#F59E0B' }} /> pending
      </span>
    );
  }
  return (
    <tr>
      <td style={{ fontWeight: 600, color: '#334155' }}>{label}</td>
      <td style={{ color: '#475569' }}>{check?.expected || <span style={{ color: '#CBD5E1' }}>—</span>}</td>
      <td style={{ color: '#475569' }}>{check?.received || <span style={{ color: '#CBD5E1' }}>—</span>}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{verdict}</td>
    </tr>
  );
};

/**
 * HIS-Status cell. Renders every status as a designed pill; RECONCILED gets the
 * success treatment, a stored MISMATCH becomes an actionable Confirm chip.
 *
 * On top of that it carries the order ↔ scan adoption affordances
 * (ORDER_ADOPTION_HANDOFF.md §5):
 *
 *  - a blue "Confirm" on an order-less scan — a study whose accession matched
 *    nothing at ingestion, so the listener invented a placeholder order from the
 *    DICOM headers while the real HIS order (if it exists) sits elsewhere with
 *    no study. Internally this is the Link Order flow; the label is "Confirm"
 *    because that is the word technicians already associate with "this row needs
 *    me". It is deliberately the SAME word as the amber mismatch chip below and
 *    the two are told apart by colour, tooltip and dialog — they can never both
 *    render on one row, because a scan-made order has no HIS order to disagree
 *    with. If you add a third chip here, keep that mutual exclusivity or the
 *    shared label stops being safe.
 *  - "Fix ID" when an automatic adoption corrected the app DB but could not
 *    reach the archive, so the images still carry the old patient identity.
 *  - inside the Confirm dialog: correct the archive's PatientID (§5.D), detach a
 *    wrongly-matched scan, and force past a modality shortfall (§5.C).
 *
 * Every one of those triggers on a STRUCTURAL condition, never on
 * po_his_status. That column cannot identify these orders: of 23 accession-less
 * scan-made orders on live data, 12 read CONFIRMED and 11 UNCONFIRMED — and the
 * pill's label is always po_his_status rather than the reconcile sub-state, so
 * NO_ITEM, PENDING and AWAITING_ACCESSION all read "UNCONFIRMED" to the user.
 */
export const HisStatusReconcileCell = ({ rec }: { rec: any }) => {
  const order = rec?.pacs_order;
  // Local overrides after a successful action — grid data refreshes later.
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const [checkOverride, setCheckOverride] = useState<any>(null);
  const [orderOverride, setOrderOverride] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accInput, setAccInput] = useState<string>('');

  // §5.C / §5.D actions, all reachable from the Confirm dialog.
  const [affirmed, setAffirmed] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [forcing, setForcing] = useState(false);
  const [detachOpen, setDetachOpen] = useState(false);
  const [detachReason, setDetachReason] = useState('');
  const [detaching, setDetaching] = useState(false);
  const [unforcing, setUnforcing] = useState(false);

  const status = statusOverride || orderOverride?.po_his_status || order?.po_his_status;
  const reconcileCheck = checkOverride || order?.po_reconcile_check;
  const hisOrdNo = orderOverride?.po_his_ord_no ?? order?.po_his_ord_no;
  const diagNo = orderOverride?.po_diag_no ?? order?.po_diag_no;

  const checks: any[] = Array.isArray(reconcileCheck?.checks) ? reconcileCheck.checks : [];
  const accCheck = checks.find(c => c?.field === 'accession');
  const pidCheck = checks.find(c => c?.field === 'patient_id');
  const modCheck = checks.find(c => c?.field === 'modality');
  const accMismatch = !!accCheck?.mismatch;
  const pidMismatch = !!pidCheck?.mismatch;
  const orderAcc = String(accCheck?.expected || orderOverride?.po_acc_no || order?.po_acc_no || '');

  const canAdopt = hasPermission(Permissions.MODIFY_STUDY_DETAILS);
  const canForce = hasPermission(Permissions.FORCE_RECONCILE);

  /**
   * §5.B trigger — the structural order-less test, exactly as isOrderLessScan()
   * defines it server-side. The one exclusion is a scan the system has already
   * declared un-linkable BY DESIGN: the order-less XA rule
   * (auto: 'ORDERLESS_MODALITY') fires for cath labs that scan straight to PACS
   * and will never have a HIS order at all, so offering to link one would put a
   * permanent chip on every cath study.
   */
  const isScanMade = isBlankish(hisOrdNo) && isBlankish(diagNo);
  const orderlessByDesign = reconcileCheck?.auto === 'ORDERLESS_MODALITY';
  const showLinkOrder = isScanMade && !orderlessByDesign && canAdopt;

  // §5.A recorded that it corrected the app DB but could not reach the archive.
  const archivePending = reconcileCheck?.archive_identity_pending;
  // Gate the chip on the permission as well as the marker: a viewer who cannot
  // act on it must fall through to the plain status pill, not to a chip that
  // opens a dialog whose every button is refused server-side.
  const showFixIdChip = !!archivePending && canAdopt;

  // A hard MISMATCH (accession or YH No. disagree) is actionable at once — both
  // are study-level and correct from the very first instance.
  const isMismatch = reconcileCheck?.status === 'MISMATCH';
  // A PENDING study that has been incomplete for longer than a scan plausibly
  // takes is also actionable: either the rest of the images are stuck in
  // transit, or the wrong scan is linked. Only a human can tell those apart, and
  // the dialog shows them the per-field comparison to do it.
  const isStalePending = reconcileCheck?.status === 'PENDING' && isStale(rec);
  const needsConfirm = isMismatch || isStalePending;

  /** Mirror a backend response onto local state AND the row record. */
  const applyResult = (data: any) => {
    if (!data) return;
    if (data.po_reconcile_check !== undefined) setCheckOverride(data.po_reconcile_check);
    if (data.po_his_status) setStatusOverride(data.po_his_status);
    setOrderOverride(prev => ({ ...(prev || {}), ...data }));
    // Other cells (e.g. the technician-assignment modality filter) read the row
    // record directly, so keep it in step without waiting for a refetch.
    if (order) Object.assign(order, data);
  };

  const openConfirmModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAccInput(orderAcc);
    setAffirmed(false);
    setForceReason('');
    setDetachOpen(false);
    setDetachReason('');
    setModalOpen(true);
  };

  const handleConfirm = () => {
    const payload: any = { pacs_ord_id: order?.pacs_ord_id };
    // Only send an accession correction when it actually changed.
    if (accMismatch && accInput.trim() && accInput.trim() !== orderAcc) {
      payload.new_acc_no = accInput.trim();
    }
    setSaving(true);
    makePostCall('/confirm-order-reconciliation', payload)
      .then(res => {
        const data = res?.data?.data;
        if (!res?.data?.success || !data) {
          message.error(res?.data?.message || 'Confirmation failed');
          return;
        }
        applyResult(data);
        if (data.po_his_status === 'RECONCILED') {
          setModalOpen(false);
          message.success('Order reconciled');
        } else {
          message.warning('Still not reconciled — see the updated field comparison.');
        }
      })
      .catch(err => {
        console.error('confirm-order-reconciliation failed', err);
        message.error(err?.response?.data?.message || 'Confirmation failed');
      })
      .finally(() => setSaving(false));
  };

  /** §5.D — write the order's (already correct) patient identity onto the images. */
  const handleFixPatientId = () => {
    setFixing(true);
    makePostCall('/correct-scan-patient-id', { pacs_ord_id: order?.pacs_ord_id, affirm: true })
      .then(res => {
        if (!res?.data?.success) {
          message.error(res?.data?.message || 'Archive update failed');
          return;
        }
        applyResult(res.data.data);
        message.success(res.data.message || 'Archive patient identity corrected.');
        setModalOpen(false);
      })
      .catch(err => {
        console.error('correct-scan-patient-id failed', err);
        message.error(err?.response?.data?.message || 'Archive update failed');
      })
      .finally(() => setFixing(false));
  };

  /** §5.C — accept a known modality shortfall. */
  const handleForce = () => {
    if (!forceReason.trim()) {
      message.info('A reason is required.');
      return;
    }
    setForcing(true);
    makePostCall('/force-reconcile-order', {
      pacs_ord_id: order?.pacs_ord_id,
      reason: forceReason.trim(),
    })
      .then(res => {
        if (!res?.data?.success) {
          message.error(res?.data?.message || 'Force reconcile failed');
          return;
        }
        applyResult(res.data.data);
        message.success('Order marked reconciled.');
        setModalOpen(false);
      })
      .catch(err => {
        console.error('force-reconcile-order failed', err);
        message.error(err?.response?.data?.message || 'Force reconcile failed');
      })
      .finally(() => setForcing(false));
  };

  const handleUnforce = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUnforcing(true);
    makePostCall('/unforce-reconcile-order', { pacs_ord_id: order?.pacs_ord_id })
      .then(res => {
        if (!res?.data?.success) {
          message.error(res?.data?.message || 'Undo failed');
          return;
        }
        applyResult(res.data.data);
        message.success('Force-reconcile undone.');
      })
      .catch(err => {
        console.error('unforce-reconcile-order failed', err);
        message.error(err?.response?.data?.message || 'Undo failed');
      })
      .finally(() => setUnforcing(false));
  };

  /** The negative branch of §5.D — the wrong scan is attached to this order. */
  const handleDetach = () => {
    if (!detachReason.trim()) {
      message.info('A reason is required to detach a scan.');
      return;
    }
    setDetaching(true);
    makePostCall('/detach-study-from-order', {
      pacs_ord_id: order?.pacs_ord_id,
      reason: detachReason.trim(),
    })
      .then(res => {
        if (!res?.data?.success) {
          message.error(res?.data?.message || 'Detach failed');
          return;
        }
        message.success(res.data.message || 'Scan detached.');
        setModalOpen(false);
      })
      .catch(err => {
        console.error('detach-study-from-order failed', err);
        message.error(err?.response?.data?.message || 'Detach failed');
      })
      .finally(() => setDetaching(false));
  };

  if (!status) return null;
  const label = String(status).replaceAll('_', ' ');

  /* ------------------------------------------------------------------ *
   * RECONCILED — all mandatory fields matched. A FORCED reconciliation says
   * who forced it and why, and can be undone: reevaluateOrderReconciliation
   * returns early on RECONCILED forever, so without an undo a mis-forced
   * order can only be repaired with SQL.
   *
   * Checked before the adoption chips only when it was NOT a scan-made order:
   * a scan-made order can read RECONCILED (its accession and patient id are
   * the study's own values by construction) while still having no HIS order
   * number at all, which means no ORU can ever be sent for it. Those still
   * need linking, so they fall through and keep the blue Confirm chip.
   * ------------------------------------------------------------------ */
  if (status === 'RECONCILED' && !showLinkOrder && !showFixIdChip) {
    const forcedBy = reconcileCheck?.forced_by;
    const tip = forcedBy
      ? `Forced by ${forcedBy}${reconcileCheck?.reason ? `: ${reconcileCheck.reason}` : ''}.`
        + (canForce ? ' Click to undo.' : '')
      : orderlessByDesign
        ? 'Order-less scan in a self-reporting modality — confirmed on the received modality alone.'
        : 'Scan fully matched this order — modality, accession and YH No. all reconciled.';

    return (
      <Tooltip title={tip}>
        <span
          className="rc-pill rc-pill--reconciled"
          data-rc-status={forcedBy ? 'FORCED' : 'RECONCILED'}
          style={{
            background: STATUS_STYLES.RECONCILED.bg,
            // An amber border keeps a forced pill visually separable from an
            // earned one without inventing a colour the palette doesn't have.
            borderColor: forcedBy ? '#FDE68A' : STATUS_STYLES.RECONCILED.border,
            color: STATUS_STYLES.RECONCILED.text,
            cursor: forcedBy && canForce ? 'pointer' : 'default',
            opacity: unforcing ? 0.6 : 1,
          }}
          role={forcedBy && canForce ? 'button' : undefined}
          onClick={forcedBy && canForce ? handleUnforce : undefined}
        >
          <CheckIcon color={STATUS_STYLES.RECONCILED.dot} />
          {forcedBy ? 'FORCED' : label}
        </span>
      </Tooltip>
    );
  }

  /* Nothing actionable: NO_ITEM / not-yet-stale PENDING keep the base pill. */
  if (!showFixIdChip && !showLinkOrder && !needsConfirm) {
    const hint =
      reconcileCheck?.status === 'NO_ITEM'
        ? 'No item code is mapped for this order — it cannot reconcile until one is added.'
        : reconcileCheck?.status === 'PENDING'
          ? 'Scan received; waiting for the remaining expected modalities.'
          : reconcileCheck?.status === 'AWAITING_ACCESSION'
            ? 'Scan arrived without an accession number — add the order and accession numbers to reconcile it.'
            : null;
    const def = STATUS_STYLES[status] || DEFAULT_STYLE;
    return <StatusPill label={label} def={def} tooltip={hint} status={status} />;
  }

  /* ------------------------------------------------------------------ *
   * Chip selection. Most urgent first:
   *   1. the images and the worklist disagree about the patient
   *   2. the scan has no HIS order behind it
   *   3. something in the comparison doesn't match
   * ------------------------------------------------------------------ */
  let chip: React.ReactNode;
  if (showFixIdChip) {
    chip = (
      <Tooltip
        title={
          'The order was matched to HIS, but the images could not be relabelled ('
          + (archivePending.error || 'archive unreachable')
          + '). Priors will not load in the viewer until this is fixed — click to retry.'
        }
      >
        <span className="rc-confirm rc-confirm--alert" role="button" onClick={openConfirmModal}>
          <WarnIcon color="#991B1B" />
          Fix ID
        </span>
      </Tooltip>
    );
  } else if (showLinkOrder) {
    chip = (
      <Tooltip
        title={
          'No HIS order is attached to this scan — the placeholder order was built from the DICOM '
          + 'headers, so it has no order number and no report can be sent back to HIS.'
          + (status === 'RECONCILED'
            ? ' Its fields match the images, but there is still no HIS order behind it.'
            : '')
          + ' Click to link the real order by its accession number.'
        }
      >
        <span
          className="rc-confirm rc-confirm--link"
          role="button"
          onClick={e => { e.stopPropagation(); setLinkOpen(true); }}
        >
          <LinkIcon color="#1D4ED8" />
          Confirm
        </span>
      </Tooltip>
    );
  } else {
    chip = (
      <Tooltip
        title={
          isMismatch
            ? 'Scan details do not match this order — click to review and confirm'
            : `Expected images are still missing after ${RECONCILE_STALE_MINUTES} minutes — click to review and re-check`
        }
      >
        <span className="rc-confirm" role="button" onClick={openConfirmModal}>
          <WarnIcon color="#B45309" />
          Confirm
        </span>
      </Tooltip>
    );
  }

  // Force-reconcile is ONLY ever right for a modality shortfall. Accession and
  // YH No. are study-level and correct from the first instance, so a
  // disagreement there is a real problem that forcing would bury — the backend
  // refuses it too, this just doesn't offer it.
  const hardMismatch = checks.some(c => c?.mismatch && c?.field !== 'modality');
  const modalityShort = !!modCheck && modCheck.matched === false;
  const showForce = canForce && modalityShort && !hardMismatch;
  const showFixId = canAdopt && (pidMismatch || !!archivePending);
  const hisPatName = order?.patient?.pat_name || 'the HIS patient';
  const hisPatPin = pidCheck?.expected || order?.patient?.pat_pin || '—';
  const scanPatPin = pidCheck?.received || null;

  return (
    <>
      {chip}

      <LinkOrderDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        pacsOrdId={order?.pacs_ord_id}
        onLinked={applyResult}
      />

      <Modal
        title="Confirm order ↔ scan match"
        open={modalOpen}
        onCancel={e => {
          (e as any)?.stopPropagation?.();
          setModalOpen(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" loading={saving} onClick={handleConfirm}>
            {accMismatch ? 'Save & Re-check' : 'Re-check'}
          </Button>,
        ]}
        width={600}
      >
        <p style={{ marginBottom: 12, color: '#64748b' }}>
          {archivePending
            ? 'This order was matched to its HIS order automatically, but the image archive could not be updated at the time — so the images still carry the old patient identity.'
            : isMismatch
              ? 'The received scan does not fully match this order.'
              : `Some expected images have still not arrived ${RECONCILE_STALE_MINUTES}+ minutes after the scan was received — they may still be syncing, or the wrong scan may be linked.`}{' '}
          Review the field comparison below
          {accMismatch ? ' and correct the accession number if the HIS-side value is wrong.' : '.'}
        </p>

        <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
          <table className="rc-tbl">
            <thead>
              <tr>
                <th>Field</th>
                <th>Order</th>
                <th>Scan</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {checks.map(c => (
                <CheckRow key={c?.field} check={c} />
              ))}
            </tbody>
          </table>
        </div>

        {accMismatch && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#334155', fontSize: 12 }}>Corrected Accession No</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                size="small"
                value={accInput}
                onChange={e => setAccInput(e.target.value)}
                placeholder="Accession number"
                style={{ maxWidth: 240 }}
              />
              {accCheck?.received && (
                <Button size="small" onClick={() => setAccInput(String(accCheck.received))}>
                  Use scan value
                </Button>
              )}
            </div>
          </div>
        )}

        {/* §5.D — the order is already linked to the right HIS order; only the
            DICOM PatientID on the images is wrong, and nothing in the app DB
            needs to change. The affirmation is the safety mechanism, not
            ceremony: a YH No. mismatch can equally mean THE WRONG PATIENT WAS
            SCANNED, and then writing the HIS ID onto these images relabels one
            patient as another. */}
        {showFixId && (
          <div style={{ marginTop: 18, padding: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#92400E', fontSize: 12 }}>
              Patient ID on the images
            </div>
            <div style={{ fontSize: 12, color: '#78350F', marginBottom: 8 }}>
              The images carry <strong>{scanPatPin || 'a different ID'}</strong>; this order belongs to{' '}
              <strong>{hisPatName}</strong> (YH {hisPatPin}). Correcting the archive is what makes the
              viewer's patient header and its prior studies agree with the worklist.
            </div>
            <Checkbox checked={affirmed} onChange={e => setAffirmed(e.target.checked)} style={{ fontSize: 12 }}>
              I confirm these images belong to {hisPatName}.
            </Checkbox>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <Button size="small" type="primary" disabled={!affirmed} loading={fixing} onClick={handleFixPatientId}>
                Correct patient ID in archive
              </Button>
              {!detachOpen && (
                <Button size="small" danger onClick={() => setDetachOpen(true)}>
                  They don't — detach this scan
                </Button>
              )}
            </div>
            {detachOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#78350F', marginBottom: 6 }}>
                  The scan comes off this order (which goes back to PENDING) and onto an unlinked
                  order, where Confirm can attach it to the correct one. Refused if a report
                  already exists.
                </div>
                <Input.TextArea
                  rows={2}
                  value={detachReason}
                  onChange={e => setDetachReason(e.target.value)}
                  placeholder="Why is this scan not this patient's? (required)"
                  style={{ fontSize: 12 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button size="small" danger loading={detaching} onClick={handleDetach}>
                    Confirm detach
                  </Button>
                  <Button size="small" onClick={() => setDetachOpen(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* §5.C — accept a modality shortfall. The nudge toward Item Codes is
            deliberate: a shortfall that keeps coming back is usually a wrong
            modality group for that centre, and forcing hides the master-data bug
            instead of fixing it. */}
        {showForce && (
          <div style={{ marginTop: 18, padding: 12, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#334155', fontSize: 12 }}>
              Force reconcile
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>
              Expected <strong>{modCheck?.expected || '—'}</strong>, received{' '}
              <strong>{modCheck?.received || 'nothing'}</strong>. Before forcing, check this item's
              modality group in Item Codes — a shortfall that keeps coming back usually means the
              group is wrong for this centre. Forcing can be undone from the green badge.
            </div>
            <Input.TextArea
              rows={2}
              value={forceReason}
              onChange={e => setForceReason(e.target.value)}
              placeholder="Why is this order reconciled despite the missing modality? (required)"
              style={{ fontSize: 12 }}
            />
            <Button
              size="small"
              type="primary"
              danger
              loading={forcing}
              disabled={!forceReason.trim()}
              onClick={handleForce}
              style={{ marginTop: 8 }}
            >
              Mark reconciled anyway
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};
